import React, { useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import { Link } from 'react-router-dom'

import logo from '../assets/recright-logo.svg'
import { ExternalLink, Subtitle } from '../typography'
import { Icon } from '../icon'
import { SearchInput } from '../search-input'
import { BasicSecondaryStyle, PrimaryFocusStyle } from '../button/base-button'

export interface NavbarProps {
  homeLink?: string
  isHomeLinkExternal?: boolean
  routerBackLink?: {
    to?: string
    href?: string
    name: string
  }
  onSearch?: (inputValue: string) => void
  searchPlaceholder?: string
  menu?: React.ReactNode
  defaultIsMenuOpen?: boolean // For testing purposes
  path?: string
  searchQuery?: string
}

const StyledNavbar = styled.div`
  height: ${({ theme }) => theme.space.s80};
  background-color: ${({ theme }) => theme.color.white};
  box-shadow: ${({ theme }) => theme.shadow.bottom};

  display: flex;
  justify-content: center;
  align-items: center;

  position: relative;
  z-index: 2;
`

const NavbarContent = styled.div<{ $isSearchOpen: boolean }>`
  width: 100%;
  padding: 0 ${({ theme }) => theme.space.s48};

  display: grid;
  grid-template-columns: 1fr auto 1fr;
  grid-template-areas: 'left center right';
  justify-content: space-between;

  ${({ theme }) => theme.mediaQuery.screenS} {
    grid-template-columns: auto auto 1fr;
  }

  // hide Recright logo on tablets
  ${({ theme }) => theme.mediaQuery.tablet} {
    grid-template-columns: 0 auto 1fr;
    padding: 0 ${({ theme }) => theme.space.s16};
  }

  // hide Recright logo and back button on small devices when search input is open
  ${({ theme }) => theme.mediaQuery.phone} {
    ${({ $isSearchOpen }) =>
      $isSearchOpen &&
      css`
        grid-template-columns: 0 0 1fr;
      `};
  }
`

const LeftSideWrapper = styled.div`
  grid-area: left;
  overflow: hidden;

  display: flex;
  align-items: center;
`

const HomeLinkStyle = css`
  display: flex;
  align-items: center;
  cursor: pointer;

  margin-right: ${({ theme }) => theme.space.s16};
  margin-left: ${({ theme }) => theme.space.s4};
  padding: 0 ${({ theme }) => theme.space.s4};

  border-radius: ${({ theme }) => theme.space.s4};
  ${PrimaryFocusStyle};
`

const BackLinkStyle = css`
  grid-area: center;
  overflow: hidden;
  display: flex;
  align-items: center;
  margin: 0 ${({ theme }) => theme.space.s16};
  text-decoration: none;

  cursor: pointer;
  user-select: none;

  padding: 0 ${({ theme }) => theme.space.s8};
  padding-right: ${({ theme }) => theme.space.s16};

  ${BasicSecondaryStyle};
  color: ${({ theme }) => theme.color.bodyText};
  border-radius: ${({ theme }) => theme.space.s2};

  &:hover {
    text-decoration: none;
    color: ${({ theme }) => theme.color.bodyText};
  }

  &.focus-visible {
    text-decoration: none;
  }
`

const HomeLink = styled(Link)`
  ${HomeLinkStyle};
`

const HomeLinkExternal = styled(ExternalLink)`
  ${HomeLinkStyle};
`

const RightSideWrapper = styled.div`
  grid-area: right;

  display: flex;
  position: relative;
  justify-content: flex-end;
`

type IconProps = {
  type?: string
}

const BackIcon = styled(Icon).attrs({
  type: 'chevron_left',
})<IconProps>`
  color: ${({ theme }) => theme.color.bodyText};
  margin-right: ${({ theme }) => theme.space.s8};
`

const BackLink = styled(Link)`
  ${BackLinkStyle};
`

const BackLinkExternal = styled(ExternalLink)`
  ${BackLinkStyle};
`

const NavbarSubtitle = styled(Subtitle)`
  margin-bottom: 0;
`

export const Navbar = ({
  homeLink,
  isHomeLinkExternal = false,
  routerBackLink,
  onSearch,
  searchPlaceholder,
  menu,
  path,
  searchQuery,
}: NavbarProps) => {
  const [isSearchOpen, setSearchOpen] = useState(false)
  const logoImage = <img src={logo} width={120} alt="Recright Logo" />

  useEffect(() => {
    if (path === '/admin/search') {
      setSearchOpen(false)
    }
  }, [path, searchQuery])

  const LinkElement = () => {
    if (homeLink) {
      if (isHomeLinkExternal) {
        return <HomeLinkExternal href={homeLink}>{logoImage}</HomeLinkExternal>
      }
      return <HomeLink to={homeLink}>{logoImage}</HomeLink>
    }
    return logoImage
  }

  return (
    <StyledNavbar>
      <NavbarContent $isSearchOpen={isSearchOpen}>
        <LeftSideWrapper>{LinkElement()}</LeftSideWrapper>
        {routerBackLink && routerBackLink.href && (
          <BackLinkExternal href={routerBackLink.href}>
            <BackIcon />
            <NavbarSubtitle>{routerBackLink.name}</NavbarSubtitle>
          </BackLinkExternal>
        )}
        {routerBackLink && routerBackLink.to && (
          <BackLink to={routerBackLink.to}>
            <BackIcon />
            <NavbarSubtitle>{routerBackLink.name}</NavbarSubtitle>
          </BackLink>
        )}
        <RightSideWrapper>
          {onSearch && (
            <SearchInput
              globalSearch
              placeholder={searchPlaceholder}
              onSearch={val => onSearch(val)}
              isOpen={isSearchOpen}
              onSetOpen={val => setSearchOpen(val)}
            />
          )}
          {menu}
        </RightSideWrapper>
      </NavbarContent>
    </StyledNavbar>
  )
}
